// Copyright 2026. WebPros International GmbH. All rights reserved.

import {
    Form,
    FormField,
    FormFieldCheckbox,
    FormFieldText,
    Icon,
    ISegmentedControlButton,
    Popover,
    Section,
    Translate,
} from '@plesk/ui-library';
import * as React from 'react';
import { ReactElement } from 'react';
import {
    IComputeResourceRecoveryReadinessStatusResponse,
    IComputeResourceVmForDisasterRecovery,
    IDestinationComputeResource,
    IDisasterRecoveryResponse,
    IStartDisasterRecoveryRequest,
    TargetSelectionPolicy,
} from 'common/api/resources/DisasterRecovery/model';
import {
    AvailableResourcesContainer,
    FormFooter,
    IncompatibleReason,
    SectionTitle,
    TargetCRSelectionGroupLabel,
    TargetCRSelectionOptionLabel,
} from 'admin/disasterRecovery/components/StartDisasterRecoveryModal/Styles';
import {
    ICONS,
    INTENT_TYPE,
    SIZE,
} from 'common/constants';
import { SegmentedControl } from 'common/components/SegmentedControl/SegmentedControl';
import SelectInput from 'common/components/Select/SelectInput';
import {
    ActionMeta,
    components,
    OptionProps,
    ValueType,
} from 'react-select';
import {
    IVariable,
    SystemVariablesPopover,
} from 'admin/common/components/SystemVariablesPopover/SystemVariablesPopover';
import ButtonWithConfirmation from 'common/components/ButtonWithConfirmation';
import { COLORS } from 'admin/core/theme';
import { AvailableResource } from 'admin/disasterRecovery/components/StartDisasterRecoveryModal/AvailableResource';
import {
    convertToDataUnit,
    DataUnit,
} from 'common/helpers/units';
import { FixedFormField } from 'admin/common/components/Filters/Styles';
import { START_DISASTER_RECOVERY_MODAL } from 'admin/disasterRecovery/constants';
import { connect } from 'react-redux';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import * as disasterRecoveryActions from 'common/modules/disasterRecovery/actions';
import { RootState } from 'admin/core/store';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import * as formErrorsActions from 'common/modules/app/formErrors/actions';
import { getProcessedErrors } from 'common/modules/app/formErrors/selectors';

export interface IStartDisasterRecoveryModal {
    onSubmit?: (recovery: IDisasterRecoveryResponse) => void;
    servers: IComputeResourceVmForDisasterRecovery[];
    disasterRecoveryReadinessStatus: IComputeResourceRecoveryReadinessStatusResponse;
}

export type StartDisasterRecoveryModalProps = IStartDisasterRecoveryModal &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

const targetSelectionButtons: Array<ISegmentedControlButton<TargetSelectionPolicy>> = [
    {
        title: <Translate content="disasterRecovery.startForm.selectionPolicy.any" />,
        value: TargetSelectionPolicy.ANY,
    },
    {
        title: <Translate content="disasterRecovery.startForm.selectionPolicy.specified" />,
        value: TargetSelectionPolicy.SPECIFIED,
    },
];

interface IAvailableResources {
    vmCount: number;
    cpu: number;
    ram: number;
    disk: number;
    isVmCountUnlimited: boolean;
    isCpuUnlimited: boolean;
    isRamUnlimited: boolean;
    isDiskUnlimited: boolean;
}

const defaultAvailableResources: IAvailableResources = {
    vmCount: 0,
    cpu: 0,
    ram: 0,
    disk: 0,
    isVmCountUnlimited: false,
    isCpuUnlimited: false,
    isRamUnlimited: false,
    isDiskUnlimited: false,
};

const DEFAULT_DISASTER_RECOVERY_NOTIFICATION_TEMPLATE = `<p>Hello,</p>
<p>We are currently experiencing an outage affecting the host of your virtual server "{{ vs_name }}". To minimize downtime, we are restoring your server to another host.</p>
<p>Restoration details:</p>
<ul>
<li>Virtual Server ID: {{ vs_id }}</li>
<li>Scheduled Time: {{ timestamp }}</li>
</ul>
<p>We apologize for any inconvenience and are working to ensure a smooth recovery.</p>
<p>If you have questions or encounter any unexpected issues following the recovery, please open a support ticket.</p>
<p>Best regards,</p>
<p>Support Team</p>`;


const defaultValues: IStartDisasterRecoveryRequest = {
    compute_resource_vm_ids: [],
    target_selection_policy: TargetSelectionPolicy.ANY,
    target_compute_resource_ids: [],
    send_notification: false,
    notification: DEFAULT_DISASTER_RECOVERY_NOTIFICATION_TEMPLATE,
};

const notificationVariables: IVariable[] = [
    {
        variable: 'vs_name',
        description: <Translate content="disasterRecovery.startForm.notification.variables.vsName" />,
    },
    {
        variable: 'vs_id',
        description: <Translate content="disasterRecovery.startForm.notification.variables.vsId" />,
    },
    {
        variable: 'timestamp',
        description: <Translate content="disasterRecovery.startForm.notification.variables.timestamp" />,
    },
];

interface IVmRequirements {
    ram: number;
    vcpu: number;
    disk: number;
}

interface IDestinationComputeResourceValueType {
    label: string;
    value: number;
}

export interface IDestinationComputeResourceOption {
    label: string;
    value: number;
    reason?: React.ReactElement;
    isDisabled: boolean;
}

export const StartDisasterRecoveryModal: React.FC<StartDisasterRecoveryModalProps> = ({
    onSubmit,
    disasterRecoveryReadinessStatus,
    servers,
    disasterRecoveryActions: {
        createDisasterRecovery,
    },
    isSubmitting,
    formErrors,
    formErrorsActions: {
        clearFormErrors,
    },
}) => {

    React.useEffect(() => () => {
        clearFormErrors();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [submitValues, setSubmitValues] = React.useState<IStartDisasterRecoveryRequest>({ ...defaultValues });

    const handleTargetSelectionChange =  async (value: TargetSelectionPolicy) => {
        setSubmitValues({
            ...submitValues,
            target_selection_policy: value,
        });
    };

    const handleCrSelectionChange = (options: ValueType<IDestinationComputeResourceValueType>, action: ActionMeta) => {
        let crIds = (options as IDestinationComputeResourceValueType[])
            .map(option => (option as IDestinationComputeResourceValueType).value);

        setSubmitValues({
            ...submitValues,
            target_compute_resource_ids: crIds,
        });
    };

    const requiredResources: IVmRequirements = React.useMemo(() => {
        let requirements: IVmRequirements = {
            ram: 0,
            vcpu: 0,
            disk: 0,
        };

        servers.forEach(server => {
            requirements.ram += server.requirements.ram;
            requirements.vcpu += server.requirements.vcpu;
            requirements.disk += server.requirements.disk;
        });

        return requirements;
    }, [servers]);

    const selectedComputeResources = React.useMemo(
        () => disasterRecoveryReadinessStatus
            .destination_compute_resources
            .filter((cr) =>
                submitValues
                    .target_compute_resource_ids
                    ?.includes(cr.compute_resource.id)
            ), [disasterRecoveryReadinessStatus, submitValues.target_compute_resource_ids]);


    const availableResources = React.useMemo(() => selectedComputeResources.reduce((res: IAvailableResources, cr) => {
        res.cpu += (cr.compute_resource.settings.limits.vcpu.total - (cr.compute_resource.settings.limits.vcpu.used ?? 0));
        res.ram += (cr.compute_resource.settings.limits.ram.total - (cr.compute_resource.settings.limits.ram.used ?? 0));
        res.disk += (cr.compute_resource.settings.limits.hdd.total - (cr.compute_resource.settings.limits.hdd.used ?? 0));
        res.vmCount += cr.compute_resource.settings.limits.vm.total;

        res.isCpuUnlimited = res.isCpuUnlimited || cr.compute_resource.settings.limits.vcpu.unlimited;
        res.isRamUnlimited = res.isRamUnlimited || cr.compute_resource.settings.limits.ram.unlimited;
        res.isDiskUnlimited = res.isDiskUnlimited || cr.compute_resource.settings.limits.hdd.unlimited;
        res.isVmCountUnlimited = res.isVmCountUnlimited || cr.compute_resource.settings.limits.vm.unlimited;

        return res;

    }, { ...defaultAvailableResources }), [selectedComputeResources]);

    const mapComputeResources = (readinessStatus: IComputeResourceRecoveryReadinessStatusResponse) => {
        let compatible: IDestinationComputeResource[] = [];
        let incompatible: IDestinationComputeResource[] = [];
        readinessStatus.destination_compute_resources.forEach((cr: IDestinationComputeResource) => {
            if (cr.compatibility_details.is_compatible) {
                compatible.push(cr);
            } else {
                incompatible.push(cr);
            }
        });

        return [{
            label: <TargetCRSelectionGroupLabel><Translate content="disasterRecovery.startForm.destinationCr.compatibleOptionsGroup" /></TargetCRSelectionGroupLabel>,
            options: compatible.map(cr => ({
                label: cr.compute_resource.name,
                value: cr.compute_resource.id,
                isDisabled: false,
            })),
        }, {
            label: <TargetCRSelectionGroupLabel><Translate content="disasterRecovery.startForm.destinationCr.incompatibleOptionsGroup" /></TargetCRSelectionGroupLabel>,
            options: incompatible.map(cr => {

                let reason: ReactElement;

                switch (false) {
                case cr.compatibility_details.destination_cr_is_active:
                    reason = <Translate content="disasterRecovery.startForm.availableResources.reasons.notActive" />;
                    break;
                case cr.compatibility_details.products_is_compatible:
                    reason = <Translate content="disasterRecovery.startForm.availableResources.reasons.productNotSupported" />;
                    break;
                case cr.compatibility_details.arch_type_is_compatible:
                    reason = <Translate content="disasterRecovery.startForm.availableResources.reasons.archType" />;
                    break;
                case !(cr.compatibility_details.missed_ip_blocks.length > 0):
                    reason = (<Translate content="disasterRecovery.startForm.availableResources.reasons.ipBlocks" params={{
                        ipBlocks: cr.compatibility_details.missed_ip_blocks
                            .map(el => el.name)
                            .slice(0, 3)
                            .join(', ') + (cr.compatibility_details.missed_ip_blocks.length > 3 ? '...' : ''),
                    }} />) ;
                    break;
                default:
                    reason = <Translate content="disasterRecovery.startForm.availableResources.reasons.default" />;
                }

                return {
                    label: cr.compute_resource.name,
                    value: cr.compute_resource.id,
                    reason,
                    isDisabled: true,
                };
            }),
        }];
    };

    const getProgress = (part: number, total: number): number => part * 100 / total;

    const vmProgress = availableResources.isVmCountUnlimited ? 100 : getProgress(
        availableResources.vmCount,
        servers.length
    );

    const cpuProgress = availableResources.isCpuUnlimited ? 100 : getProgress(
        availableResources.cpu,
        requiredResources.vcpu
    );

    const ramProgress = availableResources.isRamUnlimited ? 100 : getProgress(
        availableResources.ram,
        requiredResources.ram
    );

    const diskProgress = availableResources.isDiskUnlimited ? 100 : getProgress(
        availableResources.disk,
        requiredResources.disk
    );

    const canStartRecovery = () => {
        if (submitValues.target_selection_policy !== TargetSelectionPolicy.SPECIFIED) {
            return disasterRecoveryReadinessStatus.ready_for_recovery;
        }

        return (availableResources.isRamUnlimited || availableResources.ram >= requiredResources.ram)
            && (availableResources.isCpuUnlimited || availableResources.cpu >= requiredResources.vcpu)
            && (availableResources.isDiskUnlimited || availableResources.disk >= requiredResources.disk)
            && (availableResources.isVmCountUnlimited || availableResources.vmCount >= servers.length)
            && submitValues.target_compute_resource_ids.length > 0;
    };

    const handleConfirm = async () => {
        let submitData = {
            ...submitValues,
            compute_resource_vm_ids: servers.map((server) => server.computeResourceVm.id),
        };

        if (!submitValues.send_notification) {
            submitData.notification = null;
        }

        const recovery = await createDisasterRecovery(submitData);

        onSubmit?.(recovery.data.data);
    };

    return (
        <Form
            id="startDisasterRecoveryForm"
            applyButton={false}
            cancelButton={false}
            hideRequiredLegend={true}
            submitButton={false}
            vertical={true}
            values={submitValues}
            errors={formErrors}
        >
            <Section>
                <FormField
                    name="target_selection_policy"
                    label={<Translate content="disasterRecovery.startForm.title" />}
                >
                    <SegmentedControl
                        buttons={targetSelectionButtons}
                        selected={submitValues.target_selection_policy}
                        onChange={handleTargetSelectionChange}
                    />
                </FormField>

                <FixedFormField margin_left={'0'} data-cy={START_DISASTER_RECOVERY_MODAL.SELECT_DESTINATION_CR}>
                    <SelectInput
                        isMulti={true}
                        name="target_compute_resource_ids"
                        inputId="target-compute-resource-select"
                        placeholder={<Translate content="disasterRecovery.startForm.destinationCr.placeholder"/>}
                        options={disasterRecoveryReadinessStatus ? mapComputeResources(disasterRecoveryReadinessStatus) : []}
                        isClearable={true}
                        onChange={handleCrSelectionChange}
                        isDisabled={submitValues.target_selection_policy === TargetSelectionPolicy.ANY}
                        components={{
                            Option: (props: OptionProps<IDestinationComputeResourceOption>) => (
                                <components.Option {...props}>
                                    <TargetCRSelectionOptionLabel disabled={props.data.reason}>{props.data.label}</TargetCRSelectionOptionLabel>
                                    {props.data.reason && (
                                        <IncompatibleReason>{props.data.reason}</IncompatibleReason>
                                    )}
                                </components.Option>
                            ),
                        }}
                    />
                </FixedFormField>
            </Section>
            <Section title={
                <SectionTitle>
                    <Translate content="disasterRecovery.startForm.availableResources.title" />
                    <Popover
                        canCloseOnOutsideClick={true}
                        placement="right"
                        target={(
                            <Icon name={ICONS.INFO_CIRCLE} size="12" />
                        )}>
                        <Translate content="disasterRecovery.startForm.availableResources.tooltip" />
                    </Popover>
                </SectionTitle>
            }>
                <AvailableResourcesContainer>
                    <AvailableResource
                        progress={vmProgress}
                        header={<Translate content="disasterRecovery.startForm.availableResources.vmsCount" />}
                        middle={availableResources.isVmCountUnlimited
                            ? <Translate content="disasterRecovery.startForm.availableResources.unlimited" />
                            : availableResources.vmCount.toString()}
                        footer={availableResources.isVmCountUnlimited ? '' : <Translate content="disasterRecovery.startForm.availableResources.required" params={{
                            required: servers.length,
                        }} />}
                        muted={submitValues.target_selection_policy !== TargetSelectionPolicy.SPECIFIED}
                    />
                    <AvailableResource
                        progress={cpuProgress}
                        header={<Translate content="disasterRecovery.startForm.availableResources.cpu" />}
                        middle={availableResources.isCpuUnlimited
                            ? <Translate content="disasterRecovery.startForm.availableResources.unlimited" />
                            : availableResources.cpu.toString()}
                        footer={availableResources.isCpuUnlimited ? '' : <Translate content="disasterRecovery.startForm.availableResources.required" params={{
                            required: requiredResources.vcpu,
                        }} />}
                        muted={submitValues.target_selection_policy !== TargetSelectionPolicy.SPECIFIED}
                    />
                    <AvailableResource
                        progress={ramProgress}
                        header={<Translate content="disasterRecovery.startForm.availableResources.ram" />}
                        middle={availableResources.isRamUnlimited
                            ? <Translate content="disasterRecovery.startForm.availableResources.unlimited" />
                            : `${convertToDataUnit(availableResources.ram, DataUnit.GiB)} GiB`
                        }
                        footer={availableResources.isRamUnlimited ? '' : <Translate content="disasterRecovery.startForm.availableResources.required" params={{
                            required: `${convertToDataUnit(requiredResources.ram, DataUnit.GiB)} GiB`,
                        }} />}
                        muted={submitValues.target_selection_policy !== TargetSelectionPolicy.SPECIFIED}
                    />
                    <AvailableResource
                        progress={diskProgress}
                        header={<Translate content="disasterRecovery.startForm.availableResources.disks" />}
                        middle={availableResources.isDiskUnlimited
                            ? <Translate content="disasterRecovery.startForm.availableResources.unlimited" />
                            : `${availableResources.disk} GiB`}
                        footer={availableResources.isDiskUnlimited ? '' : <Translate content="disasterRecovery.startForm.availableResources.required" params={{
                            required: `${requiredResources.disk} GiB`,
                        }} />}
                        muted={submitValues.target_selection_policy !== TargetSelectionPolicy.SPECIFIED}
                    />
                </AvailableResourcesContainer>
            </Section>
            <FormFieldCheckbox
                label={<Translate content="disasterRecovery.startForm.notification.title" />}
                name="send_notification"
                onChange={(value: boolean) => setSubmitValues({ ...submitValues, send_notification: value })}
            />
            <FormFieldText
                name="notification"
                label={<Translate content="disasterRecovery.startForm.notification.textarea" />}
                disabled={!submitValues.send_notification}
                multiline
                rows={8}
                size={SIZE.FILL}
                onChange={(value: string) => setSubmitValues({ ...submitValues, notification: value })}
            />

            <FormFooter>
                <ButtonWithConfirmation
                    data-cy={START_DISASTER_RECOVERY_MODAL.SUBMIT_BTN}
                    isLoading={isSubmitting}
                    disabled={!canStartRecovery()}
                    handleConfirm={handleConfirm}
                    buttonColor={COLORS.RED}
                    buttonSize={SIZE.LG}
                    buttonIntent={INTENT_TYPE.DANGER}
                    confirmationButtonGhost={false}
                    translations={{
                        title: <Translate content="disasterRecovery.startForm.submit.confirmationTitle" />,
                        button: <Translate content="disasterRecovery.startForm.submit.confirmationButton" />,
                        text: <Translate content="disasterRecovery.startForm.submit.confirmationText" params={{
                            vmsCount: servers.length,
                        }} />,
                    }}
                    confirmationButtonText={(
                        <Translate content="disasterRecovery.startForm.submit.button" />
                    )}
                />
                <SystemVariablesPopover variables={notificationVariables} />
            </FormFooter>
        </Form>
    );
};

const mapStateToProps = (state: RootState) => ({
    isSubmitting: state.app.loadingFlags.has(LOADING_FLAGS.DISASTER_RECOVERY_CREATE),
    formErrors: getProcessedErrors(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    disasterRecoveryActions: bindActionCreators(disasterRecoveryActions, dispatch),
    formErrorsActions: bindActionCreators(formErrorsActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(StartDisasterRecoveryModal);
